import { useQueryClient } from '@tanstack/react-query';
import React, { FC } from 'react';
import { Control } from 'react-hook-form';

import { ChevronDownIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../components/combobox/index';
import { ModalWrapper } from '../../../components/modal-wrapper/index';
import { Tooltip } from '../../../components/tooltip/index';
import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';
import { HttpException } from '../../../exceptions/index';
import { useAppQuery, useModalState } from '../../../hooks/index';
import { ApiService } from '../../../services/index';
import { Pool } from '../../../types/pool-plans.types';
import { AddPoolForm } from '../pool-form/index';
import { FormSchema } from './plan-form.validation';

export type PoolsItemsListProps = {
  control: Control<FormSchema>;
  data: Pool[];
  handleOpenAddPlanModal: () => void;
  isLoading: boolean;
};

const PoolsItemsList: FC<PoolsItemsListProps> = ({
  data,
  control,
  isLoading,
  handleOpenAddPlanModal,
}) => {
  if (!data || isLoading)
    return (
      <span>
        <AppFormattedMessage id="LOADING" />
      </span>
    );

  return (
    <>
      {data.map(({ name, id }, i) => (
        <FormComboboxOption
          control={control}
          key={`${name}_${i}`}
          name="stepOne.pool"
          value={{ id, name }}
        >
          <Tooltip content={name}>
            <span className="truncate text-sm text-gray-700">{name}</span>
          </Tooltip>
        </FormComboboxOption>
      ))}
      <Button
        className="h-[37px] w-full justify-start rounded bg-brand-25 px-[6px] text-start text-sm font-[450] text-brand-700"
        onClick={handleOpenAddPlanModal}
        styleType="NONE"
      >
        + <AppFormattedMessage id="ADD_NEW_POOL" />
      </Button>
    </>
  );
};

export type PoolComboboxProps = {
  companyId: string;
  control: Control<FormSchema>;
};

export const PoolCombobox: FC<PoolComboboxProps> = ({ control, companyId }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useAppQuery<{ pools: Pool[]; totalPages: number }, HttpException>({
    queryKey: [QueryKey.GET_POOLS, companyId],
    queryFn: () =>
      ApiService.get({ endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'pools'] }),
    enabled: !!companyId,
  });
  const invalidateQuery = () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_POOLS] });
  };

  const { isOpen, toggler, handleOpenModal, handleCloseModal } = useModalState();

  return (
    <>
      <ModalWrapper control={{ open: isOpen, onOpenChange: toggler }} side="RIGHT">
        <div className="mr-6 h-screen w-[485px] border-transparent bg-transparent p-2 shadow-none">
          <AddPoolForm
            companyId={companyId}
            handleClose={handleCloseModal}
            invalidateQuery={invalidateQuery}
          />
        </div>
      </ModalWrapper>

      <FormCombobox className="relative" control={control} name={'stepOne.pool'}>
        <div className="relative">
          <FormComboboxInput
            className={'bg-gray-900'}
            control={control}
            customValue={(value) => value?.name}
            icon={<ChevronDownIcon className="mt-2" />}
            name={'stepOne.pool'}
            placeholder={<AppFormattedMessage id="SELECT_POOL" />}
            readOnly
          />
          <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" />
        </div>
        <ComboboxOptions className="w-full">
          <PoolsItemsList
            control={control}
            data={data?.pools || []}
            handleOpenAddPlanModal={handleOpenModal}
            isLoading={isLoading}
          />
        </ComboboxOptions>
      </FormCombobox>
    </>
  );
};
